"use client";
import React, { useCallback, useEffect, useRef } from "react";

/**
 * Photo of the memory page in a larger size: fits the screen (any device), with left / right arrows,
 * the arrow keys, a swipe on touch screens, and "x" / Esc / a click beside the picture to close.
 */
export default function PhotoLightbox({ photos = [], index, onChange, onClose }) {
  const touchStartX = useRef(null);
  const count = photos.length;
  const photo = photos[index];

  const go = useCallback(
    (step) => {
      if (count < 2) return;
      onChange((index + step + count) % count);
    },
    [count, index, onChange]
  );

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") go(-1);
      else if (e.key === "ArrowRight") go(1);
    };
    document.addEventListener("keydown", onKey);
    // The page behind does not scroll while the picture is open.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [go, onClose]);

  if (!photo) return null;

  const arrow =
    "absolute top-1/2 z-10 flex h-[56px] w-[40px] -translate-y-1/2 items-center justify-center rounded-lg bg-black/60 text-[32px] leading-none text-white hover:bg-black/80";

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Fotografija v večji velikosti"
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/80"
      onClick={onClose}
      onTouchStart={(e) => {
        touchStartX.current = e.touches[0].clientX;
      }}
      onTouchEnd={(e) => {
        if (touchStartX.current == null) return;
        const dx = e.changedTouches[0].clientX - touchStartX.current;
        touchStartX.current = null;
        if (Math.abs(dx) > 50) go(dx < 0 ? 1 : -1);
      }}
    >
      <button
        type="button"
        aria-label="Zapri"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        className="absolute right-3 top-3 z-10 flex h-[44px] w-[44px] items-center justify-center rounded-full bg-black/60 text-[28px] leading-none text-white hover:bg-black/80"
      >
        &times;
      </button>

      {count > 1 && (
        <button
          type="button"
          aria-label="Prejšnja fotografija"
          onClick={(e) => {
            e.stopPropagation();
            go(-1);
          }}
          className={`${arrow} left-2 tablet:left-6`}
        >
          ‹
        </button>
      )}

      <figure className="m-0 flex max-h-[88vh] w-[min(calc(100vw-110px),800px)] flex-col items-center" onClick={(e) => e.stopPropagation()}>
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={photo.fileUrl}
          alt={photo.alt || ""}
          className="max-h-[80vh] w-full rounded-[2px] object-contain shadow-lg"
        />
        <figcaption className="mt-2 text-center text-[14px] text-white/90">
          {photo.userName ? `${photo.userName} · ` : ""}
          {index + 1} / {count}
        </figcaption>
      </figure>

      {count > 1 && (
        <button
          type="button"
          aria-label="Naslednja fotografija"
          onClick={(e) => {
            e.stopPropagation();
            go(1);
          }}
          className={`${arrow} right-2 tablet:right-6`}
        >
          ›
        </button>
      )}
    </div>
  );
}
